import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ANIMATION, prefersReducedMotion } from '@/lib/animation-config'

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger)

export interface StaggerGridOptions {
  duration?: number
  stagger?: number | gsap.StaggerVars
  blur?: number
  scale?: number
  y?: number
  start?: string
  once?: boolean
}

/**
 * Stagger animation for grid/card layouts
 * Animates items appearing from a grid-based stagger pattern
 *
 * @example
 * function ProjectGrid() {
 *   const gridRef = useStaggerGrid<HTMLDivElement>('.card');
 *   return (
 *     <div ref={gridRef} className="grid grid-cols-3 gap-4">
 *       <div className="card">Project 1</div>
 *       <div className="card">Project 2</div>
 *       <div className="card">Project 3</div>
 *     </div>
 *   );
 * }
 */
export function useStaggerGrid<T extends HTMLElement>(selector: string, options: StaggerGridOptions = {}) {
  const containerRef = useRef<T>(null)

  useEffect(() => {
    if (!containerRef.current) return
    const container = containerRef.current

    const elements = containerRef.current.querySelectorAll(selector)
    if (elements.length === 0) return

    // Skip animations for reduced motion preference
    if (prefersReducedMotion()) {
      elements.forEach((el) => {
        gsap.set(el, { opacity: 1 })
      })
      return
    }

    const {
      duration = ANIMATION.duration.normal,
      stagger = {
        each: ANIMATION.stagger.normal,
        from: 'start',
        grid: 'auto',
      },
      blur = ANIMATION.blur.light,
      scale = ANIMATION.scale.subtle,
      y = ANIMATION.distance.short,
      start = 'top 85%',
      once = true,
    } = options

    // Set initial state
    gsap.set(elements, {
      opacity: 0,
      y,
      scale,
      filter: `blur(${blur}px)`,
    })

    // Animate on scroll
    gsap.to(elements, {
      opacity: 1,
      y: 0,
      scale: 1,
      filter: 'blur(0px)',
      duration,
      stagger,
      ease: ANIMATION.ease.reveal,
      scrollTrigger: {
        trigger: container,
        start,
        once,
      },
    })

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => {
        if (trigger.vars.trigger === container) {
          trigger.kill()
        }
      })
    }
  }, [selector, options])

  return containerRef
}
